import { Button } from '@1trading/react/components/ui/button';
import { GradientIcon } from '@1trading/react/components/ui/gradient-icon';
import { CircleX } from 'lucide-react';
import type { ComponentProps } from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { PageHeading, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import { useRouteConfig } from '@/context/route-config';
import { FuturesAppropriatenessTestResultFailedButton } from '@/features/futures/components/appropriateness-test-result-failed-button';
import FuturesAppropriatenessTestLayout from '@/features/futures/layouts/appropriateness-test';
import { useProfileFuturesAppropriateness } from '@/hooks/queries/use-profile-futures-appropriateness';
import { FuturesAppropriatenessResultStatus } from '@/services/api/profile/futures-appropriateness';
import type { FuturesAppropriatenessTestResultRouteConfig } from './appropriateness-test-result';

const FuturesAppropriatenessTestResultFailedPageRetakeEligibilityTrue = () => {
  const { reset: resetFuturesAppropriateness } = useProfileFuturesAppropriateness();
  const routeConfig = useRouteConfig<FuturesAppropriatenessTestResultRouteConfig>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleRetakeImmediately = useCallback(() => {
    resetFuturesAppropriateness();
    navigate(`${routeConfig.appropriatenessTest}?retake=true`, { replace: true });
  }, [navigate, resetFuturesAppropriateness, routeConfig.appropriatenessTest]);

  return (
    <>
      <PageHeading centered>
        <PageHeadingTitle>{t('FuturesAppropriatenessAssessment.result.failed.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('FuturesAppropriatenessAssessment.result.failed.description')}</PageHeadingDescription>
      </PageHeading>
      <footer className="flex flex-col gap-4">
        <Button size="lg" variant="primary" onClick={handleRetakeImmediately}>
          {t('FuturesAppropriatenessAssessment.result.failed.actions.retakeImmediately')}
        </Button>
        <Button size="lg" variant="outline" asChild>
          <Link to={import.meta.env.VITE_APP_EXCHANGE_URL}>{t('FuturesAppropriatenessAssessment.result.failed.actions.doNotRemindMeAgain')}</Link>
        </Button>
      </footer>
    </>
  );
};
FuturesAppropriatenessTestResultFailedPageRetakeEligibilityTrue.displayName = 'FuturesAppropriatenessTestResultFailedPageRetakeEligibilityTrue';

const FuturesAppropriatenessTestResultFailedPageRetakeEligibilityFalse = () => {
  const { t } = useTranslation();

  return (
    <>
      <PageHeading centered>
        <PageHeadingTitle>{t('FuturesAppropriatenessAssessment.result.failed.limitReached.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('FuturesAppropriatenessAssessment.result.failed.limitReached.description')}</PageHeadingDescription>
      </PageHeading>
      <footer className="flex flex-col gap-4">
        <FuturesAppropriatenessTestResultFailedButton />
        <Button size="lg" variant="outline" asChild>
          <Link to={import.meta.env.VITE_APP_EXCHANGE_URL}>{t('FuturesAppropriatenessAssessment.result.failed.actions.doNotRemindMeAgain')}</Link>
        </Button>
      </footer>
    </>
  );
};
FuturesAppropriatenessTestResultFailedPageRetakeEligibilityFalse.displayName = 'FuturesAppropriatenessTestResultFailedPageRetakeEligibilityFalse';

const FuturesAppropriatenessTestResultFailedPage = (props: ComponentProps<typeof FuturesAppropriatenessTestLayout>) => {
  const { isLoading, data: futuresAppropriateness } = useProfileFuturesAppropriateness();

  if (isLoading) return null;

  if (!futuresAppropriateness || !('result' in futuresAppropriateness) || futuresAppropriateness.result.status !== FuturesAppropriatenessResultStatus.FAILED) {
    throw new Error('Invalid futures appropriateness data');
  }

  const { status, ...retakeEligibility } = futuresAppropriateness.result;

  return (
    <FuturesAppropriatenessTestLayout className="-md:justify-center space-y-8" progress={1} size="sm" {...props}>
      <GradientIcon className="size-32 mx-auto" strokeWidth={1.5}>
        <CircleX />
      </GradientIcon>
      {retakeEligibility.canRetake ? <FuturesAppropriatenessTestResultFailedPageRetakeEligibilityTrue /> : <FuturesAppropriatenessTestResultFailedPageRetakeEligibilityFalse />}
    </FuturesAppropriatenessTestLayout>
  );
};
FuturesAppropriatenessTestResultFailedPage.displayName = 'FuturesAppropriatenessTestResultFailedPage';

export default FuturesAppropriatenessTestResultFailedPage;
